import {
  FilteredPagedQueryResult,
  FilteredPagedModel,
  EntityCommonInfoModel,
  CommonMultiDeleteModel,
  CommonEntityActiveSwitch,
} from "../../entities/common";
import { DurationHiddenSwitch, DurationModel, PackageModel } from "../../entities/subscription";
import { accountingApiSlice, TagType } from "./accountingApiSlice";
import { getQuerySearchParams } from "../../utils";

export const packageApi = accountingApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPackages: builder.query<PackageModel[], void>({
      query: () => "/package",
      providesTags: (result = [], error, arg) => [{ type: TagType.Package, id: "LIST" }],
    }),
    getActivePackages: builder.query<Array<PackageModel>, undefined>({
      query: () => "/package/valid",
      providesTags: [{ type: TagType.Package, id: "LIST" }],
    }),
    getPackagesCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => "/package/commonInfo",
      providesTags: [{ type: TagType.Package, id: "LIST" }],
    }),
    getPackagesFilteredPaged: builder.query<FilteredPagedQueryResult<PackageModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/package/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }
        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [{ type: TagType.Package, id: "PARTIAL-LIST" }, ...result.data.map(({ id }) => ({ type: TagType.Package, id }))]
          : [{ type: TagType.Package, id: "PARTIAL-LIST" }],
    }),
    getPackage: builder.query<PackageModel, number>({
      query: (id) => `/package/${id}`,
      providesTags: (result, error, arg) => [{ type: TagType.Package, id: arg }],
    }),
    addPackage: builder.mutation({
      query: (packageModel) => ({ url: "/package", method: "POST", body: packageModel }),
      invalidatesTags: [
        { type: TagType.Package, id: "LIST" },
        { type: TagType.Package, id: "PARTIAL-LIST" },
      ],
    }),
    editPackage: builder.mutation({
      query: (packageModel) => ({ url: `/package/${packageModel.id}`, method: "PUT", body: packageModel }),
      invalidatesTags: (result, error, arg) => [
        { type: TagType.Package, id: arg.id },
        { type: TagType.Package, id: "LIST" },
      ],
    }),
    switchActivePackage: builder.mutation({
      query: (model: CommonEntityActiveSwitch) => ({
        url: `/package/switchActive/${model.id}`,
        method: "PUT",
        body: model,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagType.Package, id: arg.id },
        { type: TagType.Package, id: "LIST" },
      ],
    }),
    deletePackage: builder.mutation({
      query: (id) => ({
        url: `/package/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagType.Package, id: arg },
              { type: TagType.Package, id: "LIST" },
            ],
    }),
    deleteMultiPackages: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/package/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagType.Package,
                id,
              })),
              { type: TagType.Package, id: "LIST" },
            ],
    }),

    // duration

    getDurations: builder.query({
      query: () => "/package/duration",
      providesTags: (result = [], error, arg) => [{ type: TagType.Duration, id: "LIST" }],
    }),

    getDurationsCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => "/package/duration/commonInfo",
      providesTags: [{ type: TagType.Duration, id: "LIST" }],
    }),
    getDurationsFilteredPaged: builder.query<FilteredPagedQueryResult<DurationModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/package/duration/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }
        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [{ type: TagType.Duration, id: "PARTIAL-LIST" }, ...result.data.map(({ id }) => ({ type: TagType.Duration, id }))]
          : [{ type: TagType.Duration, id: "PARTIAL-LIST" }],
    }),
    getDuration: builder.query<PackageModel, number>({
      query: (id) => `/package/duration/${id}`,
      providesTags: (result, error, arg) => [{ type: TagType.Duration, id: arg }],
    }),
    addDuration: builder.mutation({
      query: (durationModel) => ({ url: "/package/duration", method: "POST", body: durationModel }),
      invalidatesTags: [
        { type: TagType.Duration, id: "LIST" },
        { type: TagType.Duration, id: "PARTIAL-LIST" },
      ],
    }),
    editDuration: builder.mutation({
      query: (durationModel) => ({ url: `/package/duration/${durationModel.id}`, method: "PUT", body: durationModel }),
      invalidatesTags: (result, error, arg) => [
        { type: TagType.Duration, id: arg.id },
        { type: TagType.Duration, id: "LIST" },
      ],
    }),
    switchActiveDuration: builder.mutation({
      query: (model: CommonEntityActiveSwitch) => ({
        url: `/package/duration/switchActive/${model.id}`,
        method: "PUT",
        body: model,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagType.Duration, id: arg.id },
        { type: TagType.Duration, id: "LIST" },
      ],
    }),
    switchHiddenDuration: builder.mutation({
      query: (model: DurationHiddenSwitch) => ({
        url: `/package/duration/switchHidden/${model.id}`,
        method: "PUT",
        body: model,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagType.Duration, id: arg.id },
        { type: TagType.Duration, id: "LIST" },
      ],
    }),
    deleteDuration: builder.mutation({
      query: (id) => ({
        url: `/package/duration/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagType.Duration, id: arg },
              { type: TagType.Duration, id: "LIST" },
            ],
    }),
    deleteMultiDurations: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/package/duration/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagType.Duration,
                id,
              })),
              { type: TagType.Duration, id: "LIST" },
            ],
    }),
  }),
});

export const {
  useGetPackagesQuery,
  useGetActivePackagesQuery,
  useGetPackagesCommonInfoQuery,
  useGetPackagesFilteredPagedQuery,
  useGetPackageQuery,
  useAddPackageMutation,
  useEditPackageMutation,
  useSwitchActivePackageMutation,
  useDeletePackageMutation,
  useDeleteMultiPackagesMutation,

  useGetDurationsQuery,
  useGetDurationsCommonInfoQuery,
  useGetDurationsFilteredPagedQuery,
  useGetDurationQuery,
  useAddDurationMutation,
  useEditDurationMutation,
  useSwitchActiveDurationMutation,
  useSwitchHiddenDurationMutation,
  useDeleteDurationMutation,
  useDeleteMultiDurationsMutation,
} = packageApi;
